import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  EventRepeat as EventRepeatIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { followUpAPI } from '../services/api';
import StatCard from '../components/StatCard';

const recallStatusColor = {
  overdue: 'error',
  'due-today': 'warning',
  upcoming: 'info',
};

const FollowUps = () => {
  const [tabValue, setTabValue] = useState(0);
  const [followUps, setFollowUps] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const statusParam = ['due', 'overdue', 'upcoming'][tabValue];

  const fetchFollowUps = useCallback(async () => {
    try {
      const res = await followUpAPI.getFollowUpsDue({ status: statusParam });
      setFollowUps(res.data.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch follow-ups', severity: 'error' });
    }
  }, [statusParam]);

  useEffect(() => { fetchFollowUps(); }, [fetchFollowUps]);

  const dueTodayCount = followUps.filter(f => f.recallStatus === 'due-today').length;
  const overdueCount = followUps.filter(f => f.recallStatus === 'overdue').length;

  const handleWhatsAppReminder = (patient) => {
    const phone = patient?.contact?.phone?.replace(/\D/g, '');
    if (!phone) {
      setSnackbar({ open: true, message: 'No phone number on file', severity: 'error' });
      return;
    }
    const message = encodeURIComponent(
      `Hi ${patient.name}, this is a reminder from your eye clinic for your scheduled follow-up visit. Please book your appointment at your earliest convenience.`
    );
    window.open(`https://wa.me/91${phone}?text=${message}`, '_blank');
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
            <EventRepeatIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="700">Follow-up Recall</Typography>
            <Typography variant="body2" color="text.secondary">Patients due for a scheduled follow-up visit — the retention engine</Typography>
          </Box>
          <IconButton onClick={fetchFollowUps} sx={{ border: '1px solid #E9E9F2', color: 'text.secondary', '&:hover': { bgcolor: '#F4F4F9' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatCard label="Due Today" value={dueTodayCount} color="warning" />
          <StatCard label="Overdue" value={overdueCount} color="error" />
          <StatCard label="Total in View" value={followUps.length} color="primary" />
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Due (Today + Overdue)" />
          <Tab label="Overdue Only" />
          <Tab label="Upcoming" />
        </Tabs>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Last Diagnosis</TableCell>
                <TableCell>Consulted By</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {followUps.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No follow-ups in this view</Typography>
                </TableCell></TableRow>
              ) : followUps.map((f) => (
                <TableRow key={f.consultationId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: 'info.light' }}>{f.patient?.name?.charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{f.patient?.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{f.patient?.patientId} · {f.patient?.age}y</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2">{f.patient?.contact?.phone}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{f.diagnosis || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">Dr. {f.doctor?.name}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(f.followUpDueDate).toLocaleDateString('en-IN')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.daysRemaining === 0 ? 'Today' : f.daysRemaining < 0 ? `${Math.abs(f.daysRemaining)} days overdue` : `In ${f.daysRemaining} days`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={f.recallStatus.replace('-', ' ')}
                      color={recallStatusColor[f.recallStatus]}
                      sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Send WhatsApp reminder">
                      <IconButton size="small" color="success" onClick={() => handleWhatsAppReminder(f.patient)}>
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FollowUps;
